<template>
  <van-popup
    :show="show"
    closeable
    close-icon-position="top-right"
    position="bottom"
    :style="{ height: '100%' }"
    @update:show="emit('update:show', $event)"
    @closed="emit('closed')"
  >
    <div class="breakdown-sheet">
      <div class="breakdown-sheet__body">
        <div class="breakdown-sheet__header">
          <div class="breakdown-sheet__eyebrow">{{ sourceLabel }}</div>
          <h2>{{ mode === 'edit' ? '编辑拆解' : '新增拆解' }}</h2>
        </div>

        <div class="breakdown-sheet__section">
          <div class="breakdown-sheet__label">原文信息</div>
          <div class="breakdown-sheet__source">{{ sourceText || '-' }}</div>
        </div>

        <div class="breakdown-sheet__section">
          <div class="breakdown-sheet__label">拆解分类</div>
          <div v-if="breakdownTypeOptions.length > 1" class="breakdown-sheet__type-list">
            <button
              v-for="option in breakdownTypeOptions"
              :key="option.value"
              type="button"
              :class="['breakdown-sheet__type-option', { 'is-active': localBreakdownType === option.value }]"
              @click="localBreakdownType = option.value"
            >
              {{ option.label }}
            </button>
          </div>
          <div v-else class="breakdown-sheet__type-readonly">
            {{ localBreakdownTypeLabel }}
          </div>
        </div>

        <van-cell-group inset>
          <van-field
            v-model="localContent"
            rows="7"
            autosize
            type="textarea"
            maxlength="5000"
            show-word-limit
            placeholder="输入拆解内容"
          />
        </van-cell-group>

        <article v-if="hasReview" class="breakdown-sheet__review">
          <div class="breakdown-sheet__review-header">
            <strong>AI 批注</strong>
            <span :class="['breakdown-sheet__review-rating', ratingClass]">
              {{ review.rating || '未评级' }}
            </span>
          </div>

          <section v-if="reviewErrors.length" class="breakdown-sheet__review-section">
            <div class="breakdown-sheet__review-label">纠错</div>
            <ul>
              <li v-for="item in reviewErrors" :key="item">{{ item }}</li>
            </ul>
          </section>

          <section v-if="reviewMissed.length" class="breakdown-sheet__review-section">
            <div class="breakdown-sheet__review-label">补充</div>
            <ul>
              <li v-for="item in reviewMissed" :key="item">{{ item }}</li>
            </ul>
          </section>

          <section v-if="reviewRefinedBreakdown" class="breakdown-sheet__review-section">
            <div class="breakdown-sheet__review-label">优化版拆解</div>
            <div class="breakdown-sheet__review-refined">{{ reviewRefinedBreakdown }}</div>
          </section>
        </article>
      </div>

      <div class="breakdown-sheet__actions">
        <van-button
          v-if="mode === 'edit'"
          round
          plain
          type="danger"
          :disabled="saving"
          @click="emit('delete')"
        >
          删除
        </van-button>
        <van-button
          round
          type="primary"
          :loading="saving"
          @click="emit('save', { breakdownType: localBreakdownType, breakdownContent: localContent })"
        >
          保存
        </van-button>
      </div>
    </div>
  </van-popup>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  mode: {
    type: String,
    default: 'create',
  },
  sourceType: {
    type: String,
    default: 'subtitle_selection',
  },
  sourceText: {
    type: String,
    default: '',
  },
  initialBreakdownContent: {
    type: String,
    default: '',
  },
  breakdownType: {
    type: String,
    default: 'body',
  },
  enabledBreakdownTypes: {
    type: Array,
    default: () => ['body'],
  },
  saving: {
    type: Boolean,
    default: false,
  },
  review: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits([
  'update:show',
  'closed',
  'save',
  'delete',
])

const localContent = ref('')
const localBreakdownType = ref('body')
const breakdownTypeLabelMap = {
  cover: '封面',
  title_cover: '标题+封面',
  description: '视频简介',
  body: '正文',
  hook: '钩子',
  opening: '开头',
  ending: '结尾',
}
const breakdownTypeOptions = computed(() => {
  const values = Array.isArray(props.enabledBreakdownTypes) && props.enabledBreakdownTypes.length
    ? props.enabledBreakdownTypes
    : ['body']
  return values.map((value) => ({
    value,
    label: breakdownTypeLabelMap[value] || value,
  }))
})
const sourceLabel = computed(() => {
  if (props.sourceType === 'cover') return '封面'
  if (props.sourceType === 'title') return '标题+封面'
  if (props.sourceType === 'description_selection') return '视频简介'
  return '中文字幕'
})
const localBreakdownTypeLabel = computed(() => (
  breakdownTypeLabelMap[localBreakdownType.value] || localBreakdownType.value || '-'
))
const hasReview = computed(() => Boolean(props.review?.id || props.review?.breakdownId))
const reviewErrors = computed(() => Array.isArray(props.review?.errors) ? props.review.errors.filter(Boolean) : [])
const reviewMissed = computed(() => Array.isArray(props.review?.missed) ? props.review.missed.filter(Boolean) : [])
const reviewRefinedBreakdown = computed(() => String(props.review?.refinedBreakdown || '').trim())
const ratingClass = computed(() => {
  const rating = String(props.review?.rating || '').trim()
  if (rating === '到位') return 'is-success'
  if (rating === '部分到位') return 'is-warning'
  if (rating === '偏离') return 'is-danger'
  return 'is-info'
})

watch(() => [props.show, props.initialBreakdownContent, props.breakdownType, props.enabledBreakdownTypes], ([visible]) => {
  if (visible) {
    localContent.value = String(props.initialBreakdownContent || '')
    const enabledValues = breakdownTypeOptions.value.map((item) => item.value)
    localBreakdownType.value = enabledValues.includes(props.breakdownType)
      ? props.breakdownType
      : (enabledValues[0] || 'body')
  }
}, { immediate: true })
</script>

<style scoped>
.breakdown-sheet {
  display: flex;
  flex-direction: column;
  height: 100%;
  box-sizing: border-box;
}

.breakdown-sheet__body {
  flex: 1;
  overflow-y: auto;
  padding: 22px 16px 8px;
  -webkit-overflow-scrolling: touch;
}

.breakdown-sheet__header {
  padding: 0 36px 16px 2px;
}

.breakdown-sheet__eyebrow,
.breakdown-sheet__label {
  color: var(--yt-muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.breakdown-sheet__header h2 {
  margin: 8px 0 0;
  color: var(--yt-text);
  font-size: 21px;
  line-height: 1.25;
}

.breakdown-sheet__section {
  margin-bottom: 14px;
}

.breakdown-sheet__source {
  max-height: 142px;
  margin-top: 8px;
  padding: 12px;
  overflow: auto;
  border-radius: var(--yt-radius-md);
  background: #f8fafc;
  color: var(--yt-text);
  font-size: 14px;
  line-height: 1.75;
  white-space: pre-wrap;
  word-break: break-word;
}

.breakdown-sheet__type-list {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.breakdown-sheet__type-option {
  flex: 1;
  min-width: 0;
  height: 34px;
  border: 1px solid var(--yt-line);
  border-radius: 999px;
  background: #fff;
  color: var(--yt-muted);
  font-size: 13px;
  font-weight: 700;
}

.breakdown-sheet__type-option.is-active {
  border-color: rgba(225, 29, 72, 0.32);
  background: rgba(225, 29, 72, 0.08);
  color: var(--yt-accent);
}

.breakdown-sheet__type-readonly {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  margin-top: 10px;
  padding: 0 14px;
  border: 1px solid rgba(225, 29, 72, 0.32);
  border-radius: 999px;
  background: rgba(225, 29, 72, 0.08);
  color: var(--yt-accent);
  font-size: 13px;
  font-weight: 700;
}

.breakdown-sheet__actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 12px 16px calc(env(safe-area-inset-bottom) + 16px);
  border-top: 1px solid var(--yt-line);
  background: #fff;
  flex-shrink: 0;
}

.breakdown-sheet__actions .van-button {
  flex: 1;
}

.breakdown-sheet__review {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
  padding: 14px;
  border: 1px solid rgba(14, 165, 233, 0.24);
  border-radius: var(--yt-radius-md);
  background: rgba(14, 165, 233, 0.08);
}

.breakdown-sheet__review-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.breakdown-sheet__review-header strong {
  color: var(--yt-text);
  font-size: 15px;
}

.breakdown-sheet__review-rating {
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.breakdown-sheet__review-rating.is-success {
  background: rgba(22, 163, 74, 0.12);
  color: #15803d;
}

.breakdown-sheet__review-rating.is-warning {
  background: rgba(245, 158, 11, 0.14);
  color: #b45309;
}

.breakdown-sheet__review-rating.is-danger {
  background: rgba(225, 29, 72, 0.12);
  color: #be123c;
}

.breakdown-sheet__review-rating.is-info {
  background: rgba(100, 116, 139, 0.12);
  color: #475569;
}

.breakdown-sheet__review-section {
  color: var(--yt-text);
  font-size: 14px;
  line-height: 1.75;
}

.breakdown-sheet__review-label {
  margin-bottom: 6px;
  color: var(--yt-muted);
  font-size: 12px;
  font-weight: 700;
}

.breakdown-sheet__review-section ul {
  margin: 0;
  padding-left: 18px;
}

.breakdown-sheet__review-section li + li {
  margin-top: 4px;
}

.breakdown-sheet__review-refined {
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
